import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import repaymentService from '../../services/repaymentService';
import { useToast } from '../../contexts/ToastContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { format, parseISO } from 'date-fns';

const Repayments = () => {
  const [repayments, setRepayments] = useState([]);
  const [filteredRepayments, setFilteredRepayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRepayment, setSelectedRepayment] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    notes: '',
  });

  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { currency, formatCurrency } = useCurrency();

  useEffect(() => {
    fetchRepayments();
  }, []);

  useEffect(() => {
    filterRepayments();
  }, [searchTerm, statusFilter, repayments]);

  const fetchRepayments = async () => {
    try {
      setLoading(true);
      const data = await repaymentService.getAllRepayments();
      setRepayments(data);
    } catch (error) {
      showError('Erreur lors du chargement des remboursements');
    } finally {
      setLoading(false);
    }
  };

  const filterRepayments = () => {
    let filtered = repayments;

    if (searchTerm) {
      filtered = filtered.filter(repayment =>
        (repayment.loan?.loan_number || repayment.loan_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (repayment.loan?.client?.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (repayment.loan?.client?.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (repayment.loan?.client?.client_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (repayment.client_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(repayment => repayment.status === statusFilter);
    }

    setFilteredRepayments(filtered);
  };

  const handleProcessPayment = async () => {
    if (!selectedRepayment) return;

    try {
      await repaymentService.processRepayment(selectedRepayment.id, {
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        notes: paymentData.notes,
        payment_date: new Date().toISOString().split('T')[0],
      });
      
      showSuccess('Paiement enregistré avec succès');
      setShowPaymentModal(false);
      setPaymentData({ amount: '', payment_method: 'cash', notes: '' });
      fetchRepayments();
    } catch (error) {
      showError('Erreur lors du traitement du paiement');
    }
  };

  const handleWaivePenalty = async (repaymentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler la pénalité ?')) {
      try {
        await repaymentService.waivePenalty(repaymentId, {
          reason: 'Annulation administrative'
        });
        showSuccess('Pénalité annulée avec succès');
        fetchRepayments();
      } catch (error) {
        showError('Erreur lors de l\'annulation de la pénalité');
      }
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      paid: { label: 'Payé', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      partial: { label: 'Partiel', color: 'bg-blue-100 text-blue-800', icon: CurrencyDollarIcon },
      overdue: { label: 'En retard', color: 'bg-red-100 text-red-800', icon: XCircleIcon },
    };
    return configs[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: ClockIcon };
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const due = parseISO(dueDate);
    return due < today;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des remboursements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Remboursements</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestion des paiements et recouvrements
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-green-100 p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">À collecter</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {repayments
                    .filter(r => r.status === 'pending')
                    .reduce((sum, r) => sum + (r.total_amount || 0), 0)
                    .toLocaleString('fr-FR')} {currency}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-blue-100 p-3">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Payés aujourd'hui</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {repayments
                    .filter(r => r.payment_date && format(parseISO(r.payment_date), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd'))
                    .reduce((sum, r) => sum + (r.amount_paid || 0), 0)
                    .toLocaleString('fr-FR')} {currency}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-red-100 p-3">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En retard</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {repayments
                    .filter(r => isOverdue(r.due_date))
                    .reduce((sum, r) => sum + (r.total_amount || 0), 0)
                    .toLocaleString('fr-FR')} {currency}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-yellow-100 p-3">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Échéances demain</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {repayments
                    .filter(r => {
                      if (!r.due_date) return false;
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return format(parseISO(r.due_date), 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd');
                    }).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rechercher
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="input-field pl-10"
                  placeholder="N° prêt, client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Statut
              </label>
              <select
                className="input-field mt-1"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="paid">Payé</option>
                <option value="partial">Partiel</option>
                <option value="overdue">En retard</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date d'échéance
              </label>
              <select className="input-field mt-1">
                <option value="all">Toutes les dates</option>
                <option value="today">Aujourd'hui</option>
                <option value="tomorrow">Demain</option>
                <option value="this_week">Cette semaine</option>
                <option value="overdue">En retard</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Repayments Table */}
      <div className="card">
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° Prêt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Échéance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant dû
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pénalité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRepayments.map((repayment) => {
                  const status = getStatusConfig(repayment.status);
                  const Icon = status.icon;
                  const isDueOverdue = isOverdue(repayment.due_date);

                  return (
                    <tr key={repayment.id} className={`hover:bg-gray-50 ${isDueOverdue ? 'bg-red-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {repayment.loan?.loan_number || repayment.loan_number || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Échéance {repayment.installment_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {repayment.loan?.client
                            ? `${repayment.loan.client.first_name || ''} ${repayment.loan.client.last_name || ''}`.trim()
                            : repayment.client_name || 'Client non renseigné'}
                        </div>
                        {repayment.loan?.client?.client_number && (
                          <div className="text-xs text-gray-500">{repayment.loan.client.client_number}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm text-gray-900">
                              {repayment.due_date ? format(parseISO(repayment.due_date), 'dd/MM/yyyy') : '-'}
                            </div>
                            {isDueOverdue && (
                              <div className="text-xs text-red-600">
                                En retard: {Math.floor((new Date() - parseISO(repayment.due_date)) / (1000 * 60 * 60 * 24))} jours
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(repayment.total_amount)}
                        </div>
                        {repayment.amount_paid > 0 && (
                          <div className="text-xs text-gray-500">
                            Payé: {formatCurrency(repayment.amount_paid)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(repayment.penalty_amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Icon className="h-4 w-4 mr-1" />
                          <span className={`badge ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {(repayment.status === 'pending' || repayment.status === 'partial' || isDueOverdue) && (
                            <button
                              onClick={() => {
                                setSelectedRepayment(repayment);
                                setPaymentData({
                                  amount: repayment.total_amount - repayment.amount_paid,
                                  payment_method: 'cash',
                                  notes: '',
                                });
                                setShowPaymentModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              Payer
                            </button>
                          )}
                          {repayment.penalty_amount > 0 && repayment.status !== 'paid' && (
                            <button
                              onClick={() => handleWaivePenalty(repayment.id)}
                              className="text-yellow-600 hover:text-yellow-900"
                            >
                              Annuler pénalité
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredRepayments.length === 0 && (
              <div className="text-center py-12">
                <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun remboursement trouvé</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Essayez de modifier vos critères de recherche.'
                    : 'Aucun remboursement à afficher.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedRepayment && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Enregistrer le paiement
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Prêt: {selectedRepayment.loan?.loan_number || selectedRepayment.loan_number || '-'}</p>
                  <p className="text-sm text-gray-600">
                    Client: {selectedRepayment.loan?.client
                      ? `${selectedRepayment.loan.client.first_name || ''} ${selectedRepayment.loan.client.last_name || ''}`.trim()
                      : selectedRepayment.client_name || 'Client non renseigné'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Montant dû: {formatCurrency(selectedRepayment.total_amount)}
                  </p>
                  <p className="text-sm text-gray-600">
                    Déjà payé: {formatCurrency(selectedRepayment.amount_paid)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Montant à payer ({currency})
                  </label>
                  <input
                    type="number"
                    className="input-field mt-1"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    min="0"
                    max={selectedRepayment.total_amount - selectedRepayment.amount_paid}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Mode de paiement
                  </label>
                  <select
                    className="input-field mt-1"
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                  >
                    <option value="cash">Espèces</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="transfer">Virement</option>
                    <option value="check">Chèque</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    className="input-field mt-1"
                    rows={2}
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    placeholder="Informations supplémentaires"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedRepayment(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleProcessPayment}
                  className="btn-primary"
                  disabled={!paymentData.amount || parseFloat(paymentData.amount) <= 0}
                >
                  Enregistrer le paiement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Repayments;
